import React, { useEffect, useMemo, useCallback, useState } from "react"

/*HELPERS*/
import update, { extend } from "immutability-helper"

import { getValue } from "helpers/Objects"
import { IsFunction } from "helpers/ValueTests"

extend("$auto", function (value, object) {
    return object ? update(object, value) : update({}, value)
})

const useEventListener = (
    eventName,
    handler,
    element = window,
    options = {}
) => {
    // Create a ref that stores handler
    const savedHandler = React.useRef(null)

    // Update ref.current value if handler changes.
    // This allows our effect below to always get latest handler ...
    // ... without us needing to pass it in effect deps array ...
    // ... and potentially cause effect to re-run every render.
    React.useEffect(() => {
        savedHandler.current = handler
    }, [handler])

    React.useEffect(
        () => {
            // Make sure element supports addEventListener
            // On
            console.log(element, savedHandler)

            const isSupported = element && element.addEventListener
            console.log("isSupported", isSupported)

            if (!isSupported) return

            // Create event listener that calls handler function stored in ref
            const eventListener = (event) => savedHandler.current(event)

            // Add event listener
            element.addEventListener(eventName, eventListener, options)

            // Remove event listener on cleanup
            return () => {
                element.removeEventListener(eventName, eventListener)
            }
        },
        [eventName, element] // Re-run if eventName or element changes
    )
}

const getStoreValue = (store, key, def = null) => {
    return getValue(store.getState(), key, def)
}

const setStoreValue = (store, key, value) => {
    const stateDiff = {}
    const currentState = store.getState()

    const keys = key.split(".")
    let lastPart = stateDiff

    /* NEW FUNCTIONALITY WITH AUTO-CREATING PATH */
    let _value = value

    keys.forEach((keyName, index) => {
        if (keys.length === 1) {
            stateDiff["$auto"] = {
                [keyName]: { $set: {} },
            }

            lastPart = lastPart.$auto[keyName]
        } else if (index === keys.length - 1) {
            lastPart.$auto[keyName] = { $set: {} }

            lastPart = lastPart.$auto[keyName]
        } else if (index > 0) {
            lastPart.$auto[keyName] = { $auto: {} }

            lastPart = lastPart.$auto[keyName]
        } else {
            lastPart[keyName] = { $auto: {} }

            lastPart = lastPart[keyName]
        }
    })

    lastPart.$set = _value

    /* OLD FUNCTIONALITY */

    // keys.forEach((keyName) => {
    //     lastPart[keyName] = {}

    //     lastPart = lastPart[keyName]
    // })

    // lastPart.$set = value

    try {
        store.dispatch({
            type: "updateStore",
            payload: update(currentState, stateDiff),
        })

        // const nextState = store.getState()

        // const differences = diff.diff(currentState, nextState)

        // console.log("[setStoreValue]", differences, {
        //     diff: stateDiff,
        //     prevState: currentState,
        //     currentState: store.getState(),
        // })
    } catch (e) {
        console.error("Fail update store")
        console.error("Prev state: ", currentState)
        console.error("Diff state: ", stateDiff)
        console.error("Error:", e)
    }

    return stateDiff
}

const useStore = (store, key, def = null) => {
    const [value, setValue] = React.useState(getStoreValue(store, key, def))

    // console.log(`[useStore][${key}]`,{
    //     value
    // })

    const safeValue = React.useRef(value)

    React.useEffect(() => {
        function handleChangeStore() {
            const state = store.getState()

            const currentValue = getValue(state, key, def)

            if (safeValue.current !== currentValue) {
                safeValue.current = currentValue
                setValue(currentValue)

                // console.log(`[useStore][${key}][Update value]`,{
                //     value: currentValue
                // })
            }
        }

        const unsubscribe = store.subscribe(handleChangeStore)

        return () => {
            unsubscribe(handleChangeStore)
        }
    }, [key])

    return value
}

const useStoreController = (store, key, def = null) => {
    const value = useStore(store, key, def)

    const getValue = useCallback(() => {
        return getStoreValue(store, key, def)
    }, [])

    const setValue = useCallback((_value) => {
        if (IsFunction(_value)) {
            let newValue = _value(getStoreValue(store, key, def))

            const nextValue = IsFunction(newValue) ? () => newValue : newValue

            const stateDiff = setStoreValue(store, key, nextValue)

            console.log("[useStoreController][setValue][Dispatch]", stateDiff)
        } else {
            const stateDiff = setStoreValue(store, key, _value)

            console.log("[useStoreController][setValue][Dispatch]", stateDiff)
        }
    }, [])

    return [value, setValue, getValue]
}

const usePrevious = (value, defValue) => {
    const ref = React.useRef(defValue)

    React.useEffect(() => {
        ref.current = value
    })

    return ref.current
}

const useTimeout = (cb, delay) => {
    const callbackRef = React.useRef(cb)

    // Remember the latest callback.
    React.useEffect(() => {
        callbackRef.current = cb
    }, [cb])

    // Set up the interval.
    React.useEffect(() => {
        const tick = () => {
            callbackRef.current()
        }

        if (delay !== null) {
            const id = setTimeout(tick, delay)

            return () => clearTimeout(id)
        }
    }, [delay])
}

const useStateRef = (def = null) => {
    const [value, _setValue] = React.useState(def)

    const refValue = React.useRef(def)

    const setValue = React.useCallback(
        (_v) => {
            const isFunc = _v && {}.toString.call(_v) === "[object Function]"

            if (isFunc) {
                _v = _v(refValue.current)

                refValue.current = _v

                _setValue(_v)
            } else {
                refValue.current = _v

                _setValue(_v)
            }
        },
        [_setValue]
    )

    return [value, setValue, refValue]
}

const useCallbackRef = (cb, depends = []) => {
    const callback = React.useCallback(cb, depends)

    const refCallback = React.useRef(callback)

    React.useEffect(() => {
        refCallback.current = callback
    }, [callback])

    return [callback, refCallback]
}

const useBoolean = (def_value) => {
    const [value, setValue] = useState(def_value)

    const setTrue = useCallback(() => {
        setValue(true)
    }, [setValue])

    const setFalse = useCallback(() => {
        setValue(false)
    }, [setValue])

    const toggle = useCallback(() => {
        setValue((_v) => !_v)
    }, [setValue])

    return {
        value,
        setValue,
        setTrue,
        setFalse,
        toggle,
    }
}

const useRefBy = (by) => {
    const refBy = React.useRef(by)

    React.useEffect(() => {
        refBy.current = by
    }, [by])

    return refBy
}

const useRefsBy = (byList) => {
    const deps = Object.values(byList)

    const _refs = Object.keys(byList).reduce((_refs, key) => {
        return {
            ..._refs,
            [key]: useRefBy(byList[key]),
        }
    }, {})

    return useMemo(() => {
        return _refs
    }, deps)
}

const r = (ref) => ref.current
const sr = (ref, value) => {
    ref.current = value
}

// eslint-disable-next-line @typescript-eslint/no-empty-function
const noop = () => {}

const getLocalStorageValue = (name, def_value = null, json = false) => {
    const value = window.localStorage.getItem(name)

    if (value === undefined || value === null) {
        return def_value
    }

    return json ? JSON.parse(value) : value
}

const useLocalStorage = (name, def = null, json = false) => {
    const [value, _setValue] = React.useState(
        getLocalStorageValue(name, def, json)
    )

    const setValue = React.useCallback(
        (_value) => {
            console.log("[useLocalStorage][setValue]", _value, { json })

            window.localStorage.setItem(
                name,
                json ? JSON.stringify(_value) : _value
            )

            _setValue(_value)
        },
        [name, _setValue]
    )

    console.log("[useLocalStorage]", value)

    return [value, setValue]
}

export const useAsyncComponent = (loadFn, exportName = "default") => {
    const [cmp, setCmp] = useState(null)

    useEffect(() => {
        const loadCmp = async () => {
            const { [exportName]: _cmp } = await loadFn()

            setCmp(_cmp)
        }

        loadCmp()
    }, [])

    return cmp
}

export const useAsyncComponentController = (loadFn, exportName = "default") => {
    const [cmp, setCmp] = useState(null)
    const isLoading = useBoolean(false)

    const loadCmp = useCallback(async () => {
        isLoading.setTrue()

        const { [exportName]: _cmp } = await loadFn()

        isLoading.setFalse()

        setCmp(_cmp)
    }, [exportName, loadFn])

    return [cmp, isLoading.value, loadCmp]
}

export {
    r,
    sr,
    noop,
    useLocalStorage,
    useTimeout,
    useEventListener,
    useStore,
    usePrevious,
    useStateRef,
    useCallbackRef,
    useRefBy,
    useRefsBy,
    useBoolean,
    useStoreController,
    getStoreValue,
    setStoreValue,
}
